import { useState } from 'react';
import { Typography, InputLabel, FormControl, TextField, Button, TextareaAutosize, IconButton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import LocalizationProvider from '@mui/lab/LocalizationProvider';
import DesktopDatePicker from '@mui/lab/DesktopDatePicker';
import AdapterDateFns from '@mui/lab/AdapterDateFns';
import Autocomplete from '@mui/material/Autocomplete';
import { Box } from '@mui/system';
import { KYB, Analytics } from 'lib/api';
import { KYBStepBanner } from './KYBStepBanner';
import StatusSnackbar from 'components/shared/StatusSnackbar';
import CircularComponent from 'components/shared/CircularComponent';
import dayjs from 'dayjs';
import CloseIcon from '@mui/icons-material/Close';
import { KYX_ANALYTICS_EVENTS } from '../StartKYX';
import naicsCodes from './Naics/naicsCodes.json';

const useStyles = makeStyles({
    dialogBody: {
        display: 'flex',
        flexDirection: 'row',
        minHeight: '600px',
        height: '100%'
    },
    content: {
        margin: '2em',
        width: '40em'
    },
    contentTitle: {
        fontWeight: '700!important'
    },
    phoneBox: {
        marginTop: '2em'
    },
    phoneInput: {
        width: '50%'
    },
    verifyButton: {
        color: 'white!important',
        borderRadius: '25px!important',
        padding: '.5em 3em!important',
        margin: '.5em!important',
        background: '#1BABE2!important'
    },
    inputFormControl: {
        background: '#FFFFFF',
        border: '1px solid #D8DDE2',
        boxSizing: 'border-box',
        borderRadius: '3px',
        marginTop: '10px',
        width: '100%!important',
        height: '40px'
    },
    contactInforFormLabel: {
        color: '#225384',
        fontWeight: 600
    },
    businessAdressBox: {
        display: 'grid',
        gridTemplateColumns: 'repeat(2,1fr)',
        rowGap: '1em',
        columnGap: '1em'
    },
    formControl: {
        width: '100%!important'
    },
    informationOptions: {
        marginTop: '3em',
        display: 'flex',
        justifyContent: 'space-between'
    },
    next: {
        color: 'white!important',
        borderRadius: '25px!important',
        marginLeft: '1em',
        padding: '.5em 3em!important'
    }
});

export const KYBThirdStep = ({
    handleClose,
    activePage,
    handleInputChange,
    formValues,
    decreasePage,
    incrementPage,
    setFormValues,
    entities,
    industries
}) => {
    const classes = useStyles();
    const [openAlert, setOpenAlert] = useState(false);
    const [label, setLabel] = useState('');
    const [loading, setLoading] = useState(false);
    const [isValidNaicsCode, setIsValidNaicsCode] = useState(false);

    const sendInfo = () => {
        const body = {
            step: KYX_ANALYTICS_EVENTS.STEP_6,
            eventData: formValues
        };
        Analytics.createKYXEvent(body).catch(error => console.error(error));
        let val = { ...formValues };
        val.formation_date = dayjs(val.formation_date).format('YYYY-MM-DD');
        let payload = {
            id_entity_type: val.id_entity_type,
            naics_code: val.naics_code,
            legal_name: val.legal_name,
            description: val.description,
            phone_number: '+1' + val.phone_number,
            website_url: val.website_url,
            ein: val.ein,
            formation_date: val.formation_date,
            address: {
                city: val.city,
                line1: val.line1,
                line2: val.line2,
                state: val.state,
                country: 'US',
                postalCode: val.postalCode
            }
        };

        setLoading(true);
        // Business Created
        if (val.id) {
            KYB.updateBusiness(payload)
                .then(() => {
                    setLoading(false);
                    incrementPage();
                })
                .catch(error => {
                    // Error
                    setLoading(false);
                    incrementPage();
                });
            return;
        }

        KYB.createBusiness(payload)
            .then(response => {
                setFormValues({
                    ...formValues,
                    id: response.id
                });
                setLoading(false);
                incrementPage();
                window.dataLayer.push({
                    event: 'virtual-card-kyb-success'
                });
            })
            .catch(error => {
                setLoading(false);
                if (error?.response?.data?.type === 'Business/InvalidEIN') {
                    setOpenAlert(true);
                    setLabel('Invalid Business Employee Identification Number (EIN)');
                    /*decreasePage();*/
                    return;
                }
                if (error?.response?.data?.type === 'KYB/AlreadyCreated') {
                    setOpenAlert(true);
                    setLabel('KYB already created');
                    /*decreasePage();*/
                    return;
                }
                setLabel(error?.response?.data?.message);
                setOpenAlert(true);
            });
    };

    const handleEntityChange = (e, newVal) => {
        setFormValues({
            ...formValues,
            id_entity_type: newVal ? newVal.id : ''
        });
    };

    const handleNaicsCodeChange = event => {
        setFormValues({
            ...formValues,
            naics_code: event.target.value
        });

        if (naicsCodes.some(naicsCode => naicsCode.code === Number(event.target.value)) && event.target.value.length === 6) {
            setIsValidNaicsCode(true);
        } else {
            setIsValidNaicsCode(false);
        }
    };

    const convertDate = date => {
        let validDate = dayjs(date).isValid();
        setFormValues({
            ...formValues,
            formation_date: validDate ? date : null
        });
    };

    const idToEntityType = id => {
        if (!entities.length) return null;
        if (!id) return null;
        return entities.filter(item => item.id === id)[0];
    };

    const isNextDisabled = () => {
        return !formValues.id_entity_type || !isValidNaicsCode || !formValues.formation_date || !formValues.description;
    };

    return (
        <Box className={classes.dialogBody}>
            <KYBStepBanner activeStep={activePage} incrementStep={() => incrementPage()} />
            <Box className={classes.content}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Box>
                        <Typography variant="h5" className={classes.contentTitle}>
                            Business Description
                        </Typography>
                        <Typography variant="subtitle1" style={{ color: '#A8B6C1' }}>
                            This will help us verify your business quickly
                        </Typography>
                    </Box>
                    <IconButton
                        edge="start"
                        color="inherit"
                        onClick={handleClose}
                        aria-label="close"
                        sx={{ alignSelf: 'flex-start' }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        marginTop: { lg: '45px', md: '45px', sm: 'auto', xs: 'auto' }
                    }}
                    flexDirection="column">
                    <FormControl className={classes.formControl} style={{ rowGap: '.5em' }}>
                        <Typography variant="subtitle2">Entity Type</Typography>
                        <Autocomplete
                            disablePortal
                            id="entityType"
                            autoSelect={true}
                            options={entities}
                            getOptionLabel={option => option.name}
                            sx={{
                                width: {
                                    lg: 300,
                                    md: 300,
                                    sm: '100%',
                                    xs: '100%'
                                }
                            }}
                            renderInput={params => <TextField {...params} placeholder="Entities" />}
                            onChange={handleEntityChange}
                            value={idToEntityType(formValues.id_entity_type)}
                            size="small"
                            variant="outlined"
                        />
                    </FormControl>
                </Box>
                <Box marginTop="20px" flexDirection="column">
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <InputLabel
                            sx={{
                                color: '#225384',
                                fontWeight: 600,
                                marginBottom: '.5em',
                                fontSize: {
                                    lg: '13px',
                                    md: '13px'
                                },
                                gridColumn: '1/3'
                            }}>
                            Formation Date
                        </InputLabel>
                        <DesktopDatePicker
                            inputFormat="MM/dd/yyyy"
                            maxDate={new Date()}
                            value={formValues.formation_date}
                            onChange={e => convertDate(e)}
                            renderInput={params => (
                                <TextField
                                    {...params}
                                    size="small"
                                    sx={{
                                        width: {
                                            sm: '60%',
                                            xs: '60%'
                                        }
                                    }}
                                />
                            )}
                        />
                    </LocalizationProvider>
                </Box>
                <Box marginTop="20px" flexDirection="column">
                    <FormControl style={{ width: '100%' }}>
                        <Typography variant="subtitle2">Business Description</Typography>
                        <TextareaAutosize
                            name="description"
                            id="description"
                            onChange={handleInputChange}
                            value={formValues.description}
                            minRows={4}
                            maxLength={255}
                            placeholder="Please enter a brief description of your business"
                            style={{
                                height: '60px',
                                fontSize: '13px',
                                fontWeight: 500,
                                lineHeight: '20px',
                                padding: '4px 10px 8px 10px',
                                border: '1px solid #D8DDE2',
                                borderRadius: '4px'
                            }}
                        />
                    </FormControl>
                </Box>
                <Box marginTop="20px" flexDirection="column">
                    <FormControl className={classes.formControl} style={{ rowGap: '.5em' }}>
                        <Box display="flex" alignItems="center">
                            <Typography variant="subtitle2" sx={{ marginRight: '130px' }}>
                                NAICS Code
                            </Typography>
                            <a
                                href="https://www.naics.com/search/"
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{ textDecoration: 'none' }}>
                                <Typography sx={{ fontSize: '12px', color: '#1BABE2', fontWeight: 'bold' }}>
                                    Find my code
                                </Typography>
                            </a>
                        </Box>
                        <TextField
                            name="naics_code"
                            id="naics_code"
                            onChange={handleNaicsCodeChange}
                            value={formValues.naics_code === 0 ? '' : formValues.naics_code}
                            minRows={4}
                            maxLength={255}
                            type="number"
                            placeholder="Enter 6 Digits NAICS Code"
                            size="small"
                            inputProps={{ maxLength: 6 }}
                            sx={{ width: '300px' }}
                        />
                    </FormControl>
                </Box>
                <Box className={classes.informationOptions}>
                    <Button variant="text" onClick={() => decreasePage()}>
                        Back
                    </Button>
                    {loading ? (
                        <CircularComponent />
                    ) : (
                        <Button variant="contained" disabled={isNextDisabled()} onClick={() => sendInfo()}>
                            Next
                        </Button>
                    )}
                </Box>
            </Box>
            <StatusSnackbar
                label={label}
                severity={'error'}
                open={openAlert}
                needsAction={true}
                decreasePage={decreasePage}
                setOpenAlert={setOpenAlert}
            />
        </Box>
    );
};
